import {
  normalizeHaklaiExpenseDepreciationByPart,
  normalizeHaklaiExpensesForDependencies
} from "../expenses/logic/haklaiExpensesHelpers";
import { removeHaklaiPropertyPartKeys } from "../propertyParts/logic/haklaiPropertyPartsHelpers";
import {
  hasRawNumericValue,
  isOptionalPartCategory
} from "./haklaiFlowHelpers";

const getIndexedPartKeySortValue = (key = "") => {
  const [, rawIndex] = String(key).split("_");
  const index = Number(rawIndex);
  return Number.isFinite(index) ? index : 0;
};

const sortPropertyPartKeysForRemoval = (keys = []) => (
  [...keys].sort((a, b) => {
    const prefixCompare = String(a).split("_")[0].localeCompare(String(b).split("_")[0]);
    if (prefixCompare !== 0) return prefixCompare;
    return getIndexedPartKeySortValue(b) - getIndexedPartKeySortValue(a);
  })
);

export const isEmptyHaklaiPropertyPartDraft = (part = {}) => {
  if (!isOptionalPartCategory(part?.category)) return false;

  const hasSelectedAssetType =
    Boolean(String(part?.assetTypeKey || "").trim())
    || Boolean(String(part?.label || "").trim());

  return (
    !hasSelectedAssetType
    && !hasRawNumericValue(part?.value)
    && !hasRawNumericValue(part?.valueWithoutZaban)
    && part?.constructionsDoneBeforeApril1997 !== true
  );
};

export const isEmptyHaklaiExpenseDraft = (expense = {}) => (
  !String(expense?.expenseType || "").trim()
  && !hasRawNumericValue(expense?.amount)
  && !expense?.expenseDate
  && !Object.keys(normalizeHaklaiExpenseDepreciationByPart(
    expense?.depreciationByPart,
    null,
    {},
    expense?.expenseType || ""
  )).length
);

export const pruneEmptyHaklaiAnalyzeDraftRows = (calcState = {}) => {
  const mainCalc = calcState.mainCalc || {};
  const hasZaban = calcState.preCalc?.hasZaban === true;
  let propertyPartsData = mainCalc.propertyPartsData || {};
  let expenses = Array.isArray(mainCalc.expenses) ? mainCalc.expenses : [];
  let residentialRentalByPart = mainCalc.residentialRentalByPart || {};
  let purchaseDepreciationByPart = mainCalc.purchaseDepreciationByPart || {};
  let purchaseDepreciationSelectionByPart = mainCalc.purchaseDepreciationSelectionByPart || {};
  let changed = false;

  const emptyPropertyPartKeys = Object.entries(propertyPartsData)
    .filter(([, part]) => isEmptyHaklaiPropertyPartDraft(part))
    .map(([key]) => key);

  if (emptyPropertyPartKeys.length > 0) {
    const removalState = removeHaklaiPropertyPartKeys({
      propertyPartsData,
      expenses,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      purchaseDepreciationSelectionByPart,
      keys: sortPropertyPartKeysForRemoval(emptyPropertyPartKeys),
      hasZaban
    });

    if (removalState.changed) {
      propertyPartsData = removalState.propertyPartsData;
      expenses = removalState.expenses;
      residentialRentalByPart = removalState.residentialRentalByPart;
      purchaseDepreciationByPart = removalState.purchaseDepreciationByPart;
      purchaseDepreciationSelectionByPart = removalState.purchaseDepreciationSelectionByPart;
      changed = true;
    }
  }

  const nextExpenses = expenses.filter((expense) => !isEmptyHaklaiExpenseDraft(expense));
  if (nextExpenses.length !== expenses.length) {
    expenses = nextExpenses;
    changed = true;
  }

  const normalizedExpensesState = normalizeHaklaiExpensesForDependencies(
    expenses,
    propertyPartsData,
    hasZaban
  );
  if (normalizedExpensesState.changed) {
    expenses = normalizedExpensesState.expenses;
    changed = true;
  }

  if (!changed) {
    return { changed: false, calc: calcState };
  }

  return {
    changed: true,
    calc: {
      ...calcState,
      mainCalc: {
        ...mainCalc,
        propertyPartsData,
        expenses,
        residentialRentalByPart,
        purchaseDepreciationByPart,
        purchaseDepreciationSelectionByPart
      }
    }
  };
};
